import { Bot, Github, GitBranch, Puzzle, Sparkles } from 'lucide-react';
import type { ReactNode } from 'react';
import type { OrgPluginCatalogEntryType } from '@/types/plugins';

export function TypeIcon({
  type,
  size = 'default',
}: {
  type?: OrgPluginCatalogEntryType;
  size?: 'default' | 'compact';
}) {
  const cls =
    size === 'compact'
      ? 'h-4 w-4 shrink-0 text-muted-foreground'
      : 'h-5 w-5 shrink-0 text-muted-foreground';
  if (type === 'agent') return <Bot className={cls} />;
  if (type === 'skill') return <Sparkles className={cls} />;
  return <Puzzle className={cls} />;
}

export function HostIcon({ repoUrl }: { repoUrl: string }) {
  const url = repoUrl.toLowerCase();
  if (url.includes('github.com')) return <Github className="h-4 w-4" />;
  return <GitBranch className="h-4 w-4" />;
}

export interface PluginGridCardProps {
  icon: ReactNode;
  title: string;
  /** Optional content to the right of the title (e.g. a status badge). */
  titleAside?: ReactNode;
  description?: string;
  repoUrl: string;
  /** Subdir suffix; surfaced in the host-icon tooltip when present. */
  subdir?: string;
  /** Pre-joined meta string shown after the host icon (e.g. "v1.2 · by Natera"). */
  metaText?: string;
  /** Right-aligned action (Install button, status text, etc.). */
  action?: ReactNode;
  /** When set, the whole card becomes clickable (e.g. for navigation). */
  onClick?: () => void;
  /**
   * `compact` trims padding, title size, and description clamp by ~25% — used
   * on the installed-plugins grid so it reads as denser than the discovery /
   * catalog grid above it.
   */
  size?: 'default' | 'compact';
}

/**
 * Shared grid card used by both plugin-page surfaces: the org catalog
 * (no onClick, action = Install button) and the installed-plugins grid
 * (onClick = navigate to detail, action = status badge). Composition over
 * options — callers build the badges/action they need and hand them in.
 */
export function PluginGridCard({
  icon,
  title,
  titleAside,
  description,
  repoUrl,
  subdir,
  metaText,
  action,
  onClick,
  size = 'default',
}: PluginGridCardProps) {
  const repoHref = repoUrl.replace(/\.git$/, '');
  const repoDisplay =
    repoUrl.replace(/^https?:\/\//, '').replace(/\.git$/, '') +
    (subdir ? ` / ${subdir}` : '');

  const compact = size === 'compact';

  // div + role=button instead of native <button> so the host-icon <a> and the
  // action's nested <button> are valid interactive descendants.
  const clickable = !!onClick;
  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      className={`text-left rounded-xl border border-border bg-card flex flex-col transition-colors hover:border-primary-500 hover:bg-accent/30 ${
        compact ? 'p-4 gap-2' : 'p-6 gap-3'
      } ${
        clickable
          ? 'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500'
          : ''
      }`}
    >
      <div className={`flex items-center ${compact ? 'gap-2' : 'gap-3'}`}>
        {icon}
        <h3
          className={`font-semibold text-foreground truncate flex-1 ${
            compact ? 'text-sm' : 'text-lg'
          }`}
        >
          {title}
        </h3>
        {titleAside && <div className="shrink-0">{titleAside}</div>}
      </div>

      {description && (
        <p
          className={`text-muted-foreground ${
            compact ? 'text-xs line-clamp-2' : 'text-sm line-clamp-3'
          }`}
        >
          {description}
        </p>
      )}

      <div
        className={`flex items-center justify-between gap-3 mt-auto ${
          compact ? 'pt-1' : 'pt-2'
        }`}
      >
        <div className="flex items-center gap-2 text-xs text-muted-foreground min-w-0">
          <a
            href={repoHref}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            aria-label="Open repository"
            title={repoDisplay}
            className="shrink-0 rounded p-1 -m-1 hover:text-foreground hover:bg-accent transition-colors"
          >
            <HostIcon repoUrl={repoUrl} />
          </a>
          {metaText && <span className="truncate">{metaText}</span>}
        </div>
        {action && (
          <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
            {action}
          </div>
        )}
      </div>
    </div>
  );
}
